import React from 'react';
import clsx from 'clsx';
import moment from 'moment/moment';
import * as Routes from 'modules/routes';
import Text from '@/components/ui/Text';
import { Footer } from '@/components/common/Modal';
import Icon from '@/components/ui/Icon/Icon';
import IImage from '@/components/interfaces/Image';
import { MediaMap } from '@/types/api/v2/MediaMapSchema';
import showToast from '@/components/common/Toast';
import useAsyncFileDownload from '@/hooks/useAsyncFileDownload';
import { DOMO_SHEETS } from './ImageInfo.constants';
import LicensePill from './LicensePill';

interface ImageInfoModalBodyProps {
  programId: number;
  image: IImage;
  mediaMaps: MediaMap[];
}

const ImageInfoModalBody = ({ programId, image, mediaMaps }: ImageInfoModalBodyProps) => {
  const { onClick: handleDownload, isLoading: isDownloading } = useAsyncFileDownload(
    image.url,
    image.image_en_file_name,
  );

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    showToast('Copied to Clipboard!', { position: 'bottom-right' });
  };

  const handleRedirect = () => {
    const imagesUrl = Routes.admin_images_path({
      'id[eq]': image.id,
      program_id: programId,
    });
    window.open(imagesUrl, '_blank');
  };

  const licenseTypes = Array.from(new Set((mediaMaps ?? []).map(item => item.license_type)));
  const isCollage = image.image_en_file_name?.toLowerCase().includes('collage');

  return (
    <div className="tw-flex tw-flex-col tw-h-full">
      <section className="tw-flex tw-items-center tw-flex-1 tw-min-h-0 tw-mb-4">
        <div
          className={clsx(
            'tw-flex tw-items-center tw-justify-center ',
            'tw-h-full tw-min-h-[112px] tw-min-w-[250px] tw-max-w-[350px]',
          )}
        >
          <img
            alt={image.description_en}
            className="tw-max-h-full tw-max-w-full tw-h-auto tw-w-auto tw-object-contain tw-rounded"
            src={image.url}
          />
        </div>
        <p className="tw-text-base tw-self-end tw-pl-3 tw-m-0">
          Image Credit: {image.credits || 'N/A'}
        </p>
      </section>

      <section className="tw-flex-shrink-0 tw-py-4">
        <Text className="tw-flex tw-items-center tw-gap-2" variant="heading5">
          <Icon icon="InfoAlternate" size="20" />
          File Information
        </Text>

        <div className="tw-grid tw-grid-cols-2 tw-gap-x-8 tw-gap-y-4">
          <div className="tw-flex tw-flex-col">
            <span className="tw-text-sm tw-text-dark-grey tw-mb-1">File Name</span>
            <div className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-font-medium">{image.image_en_file_name}</span>
              <button
                aria-label="Copy file name"
                className={clsx(
                  'tw-cursor-pointer tw-border-none tw-bg-transparent tw-p-0',
                  'tw-text-dark-grey hover:tw-text-tci-purple',
                )}
                type="button"
                onClick={() => handleCopy(image.image_en_file_name)}
              >
                <Icon icon="Copy" size="18" />
              </button>
            </div>
          </div>

          <div className="tw-flex tw-flex-col">
            <span className="tw-text-sm tw-text-dark-grey tw-mb-1">Date Uploaded</span>
            <span className="tw-font-medium">{moment(image.image_en_updated_at).format('MMM D, YYYY')}</span>
          </div>

          <div className="tw-flex tw-flex-col">
            <span className="tw-text-sm tw-text-dark-grey tw-mb-1">TCI Image ID</span>
            <span className="tw-font-medium">{image.id}</span>
          </div>

          <div className="tw-flex tw-flex-col">
            <span className="tw-text-sm tw-text-dark-grey tw-mb-1">Number of Uses</span>
            <span className="tw-font-medium">{image.num_of_uses || 'N/A'}</span>
          </div>

          <div className="tw-flex tw-flex-col">
            <span className="tw-text-sm tw-text-dark-grey tw-mb-1">File Size</span>
            <span className="tw-font-medium">{image.image_file_size}</span>
          </div>

          <div className="tw-flex tw-flex-col">
            <div className="tw-relative tw-flex tw-items-center">
              {licenseTypes.length > 1 && (
                <span className="tw-absolute tw-left-[-20px] tw-text-orange">
                  <Icon icon="WarningOutlined" size={16} />
                </span>
              )}
              <span className="tw-text-sm tw-text-dark-grey tw-mb-1">License Type</span>
            </div>
            <div className="tw-flex tw-flex-wrap tw-gap-2 tw-items-center">
              {licenseTypes.length === 0 ? (
                <LicensePill isCollage={isCollage} licenseType={null} />
              ) : (
                licenseTypes.map(licenseType => (
                  <LicensePill key={licenseType} licenseType={licenseType} />
                ))
              )}
              {licenseTypes.length > 1 && (
                <a
                  className="tw-text-dark-grey hover:tw-text-tci-purple tw-cursor-pointer"
                  href={DOMO_SHEETS.duplicateImageNamesAndLicenceInfo}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <Icon icon="ExternalLink" size="18" />
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      <hr className="!tw-m-0" />

      <section className="tw-flex-shrink-0 tw-flex tw-flex-col tw-pt-4 tw-gap-1">
        <Text className="tw-flex tw-items-center tw-gap-2" variant="heading5">
          <Icon icon="Link" size="20" />
          TCI Image URL
        </Text>

        <div
          className={clsx(
            'tw-flex tw-items-center tw-box-border tw-w-full tw-bg-light-grey tw-justify-between tw-px-3',
            'tw-rounded-md tw-border tw-border-solid tw-border-med-grey tw-h-12',
          )}
        >
          <Text
            className="tw-flex tw-h-full tw-items-center tw-overflow-x-auto tw-whitespace-nowrap tw-flex-1"
            tag="div"
          >
            {image.url}
          </Text>

          <div className="tw-flex tw-gap-3 tw-text-dark-grey tw-flex-shrink-0 tw-ml-5">
            <button
              aria-label="Copy image URL"
              className={`
                tw-cursor-pointer tw-border-none tw-bg-transparent tw-p-0 tw-text-dark-grey hover:tw-text-tci-purple
              `}
              type="button"
              onClick={() => handleCopy(image.url)}
            >
              <Icon icon="Copy" size="24" />
            </button>
            <button
              aria-label="Download image"
              className={clsx(
                'tw-cursor-pointer tw-border-none tw-bg-transparent tw-p-0',
                'tw-text-dark-grey hover:tw-text-tci-purple',
              )}
              disabled={isDownloading}
              type="button"
              onClick={handleDownload}
            >
              <Icon icon="Download" size="24" />
            </button>
          </div>
        </div>
      </section>
      <Footer
        primaryButtonCallback={handleRedirect}
        primaryButtonClassName="btn btn--link-purple no_pad"
        primaryButtonSubmit={false}
        primaryButtonText="View In Image Library"
      />
    </div>
  );
};

export default ImageInfoModalBody;
